
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, MapPin, Scissors, Search, AlertTriangle } from 'lucide-react';
import { API_BASE } from '../config';

export default function UserBookings({ t, user, lang }) {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const ubT = t.userBookings || {};

  const fetchBookings = useCallback(async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/bookings/customer/${user.email}`);
      if (!response.ok) throw new Error(ubT.error || "Erreur lors de la récupération");
      const data = await response.json();
      setBookings(data);
      setFilteredBookings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.email, ubT.error]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    let result = bookings;
    if (filterStatus !== 'all') {
      result = result.filter(b => b.status === filterStatus);
    }
    if (searchTerm) {
      result = result.filter(b =>
        b.service_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredBookings(result);
  }, [filterStatus, searchTerm, bookings]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-50 text-green-700 border-green-200';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'no-show': return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled': return 'bg-slate-50 text-slate-500 border-slate-200';
      default: return 'bg-brand-50 text-brand-700 border-brand-200';
    }
  };

  const getStatusLabel = (status) => {
    return ubT.status?.[status] || status;
  };

  const formatDate = (dateStr) => {
    const localeMap = { fr: 'fr-FR', pt: 'pt-PT', en: 'en-GB' };
    return new Date(dateStr).toLocaleDateString(localeMap[lang] || 'fr-FR', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        <p className="mt-4 text-slate-500 font-medium">{ubT.loading || 'Chargement...'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 animate-fade-in-up">
      {/* Error Message */}
      {error && (
        <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center text-red-700 gap-3">
          <AlertTriangle className="flex-shrink-0" />
          <p className="font-medium">{error}</p>
          <button 
            onClick={fetchBookings}
            className="ml-auto text-xs font-black uppercase tracking-widest bg-red-100 px-4 py-2 rounded-xl hover:bg-red-200 transition-colors"
          >
            {ubT.retry || 'Réessayer'}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-8 flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 font-serif italic">{ubT.title || 'Mes Rendez-vous'}</h1>
          <p className="text-slate-500 mt-1 font-medium">{ubT.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 bg-brand-50 px-6 py-3 rounded-2xl border border-brand-100">
          <Calendar className="text-brand-600" size={20} />
          <span className="text-brand-900 font-black uppercase text-xs tracking-widest">
            {bookings.length} {ubT.countLabel || 'Prestations'}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-4 mb-8">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder={ubT.searchPlaceholder || "Rechercher..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-brand-500 focus:bg-white outline-none transition-all font-medium text-sm"
            />
          </div>
          <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-200 overflow-x-auto no-scrollbar">
            {[
              { id: 'all', label: ubT.filterAll || 'Tous' },
              { id: 'confirmed', label: ubT.filterConfirmed || 'Confirmés' },
              { id: 'pending', label: ubT.filterPending || 'En attente' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterStatus(f.id)}
                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                  filterStatus === f.id ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {filteredBookings.length > 0 ? (
          filteredBookings.map((b) => (
            <div
              key={b.id}
              className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden hover:shadow-xl transition-all group border-l-4"
              style={{ borderLeftColor: b.status === 'confirmed' ? '#16a34a' : (b.status === 'pending' ? '#f59e0b' : '#cbd5e1') }}
            >
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-brand-50 p-3 rounded-2xl group-hover:bg-brand-600 group-hover:text-white transition-colors">
                      <Scissors size={20} className="text-brand-600 group-hover:text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900 leading-tight uppercase tracking-tighter">{b.service_type}</h3>
                      <span className={`inline-block mt-1 px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(b.status)}`}>
                        {getStatusLabel(b.status)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-slate-900 leading-none">{parseFloat(b.deposit_amount || 0).toFixed(0)}€</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">{ubT.guaranteeLabel || 'Garantie'}</p>
                  </div>
                </div>

                <div className="space-y-4 border-t border-slate-50 pt-6">
                  <div className="flex items-center text-slate-600">
                    <Calendar className="mr-3 text-brand-500" size={16} />
                    <span className="text-sm font-bold capitalize">{formatDate(b.booking_date)}</span>
                  </div>

                  <div className="flex items-center text-slate-600">
                    <Clock className="mr-3 text-brand-500" size={16} />
                    <span className="text-sm font-black">{b.booking_time}</span>
                  </div>

                  <div className="flex items-start text-slate-600">
                    <MapPin className="mr-3 text-brand-500 mt-1" size={16} />
                    <span className="text-sm font-medium leading-relaxed">{b.address}</span>
                  </div>
                </div>

                {b.notes && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-2xl border border-slate-100 italic text-xs text-slate-500">
                    "{b.notes}"
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="md:col-span-2 bg-white rounded-[3rem] shadow-sm border border-slate-100 p-20 text-center">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-200">
                <Calendar size={48} />
            </div>
            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">
              {ubT.emptyTitle || 'Aucun rendez-vous trouvé'}
            </h3>
            <p className="text-slate-400 font-medium">
              {searchTerm || filterStatus !== 'all'
                ? ubT.emptySearch || 'Essayez de modifier vos filtres de recherche'
                : ubT.emptyNoBookings || 'Vous n\'avez pas encore effectué de réservation.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
